import { defineStore } from 'pinia';
import { ref } from 'vue';

import {
  getCloudinaryUrlForTransformation,
  getCloudinaryUrlVersions,
  HgCloudinaryNamedTransformation,
} from '@/helpers/Cloudinary/CloudinaryHelper';
import { getListingImages } from '@/helpers/ListingData/ListingData';
import {
  getRestageErrorType,
  postRestageImage,
  type RestageRequestPayload,
  type RestageTransformation,
} from '@/helpers/Restage/client';
import {
  OVERTIME_THRESHOLD,
  restageImageCallFailed,
  restageImageCallOvertime,
  restageImageCallRecovered,
  restageImageCallStarted,
  restageImageCallSucceeded,
} from '@/helpers/Restage/metrics';
import { useListingStore } from '@/store/modules/listing';
import type { HgImage } from '@/store/types';
import { get, has, set, unset } from 'lodash-es';

// Nested Object: imageUrl -> transformation -> restagedUrl
type TransformationUrlCache = Partial<Record<RestageTransformation, string>>;
type RestagedUrlCache = Record<string, TransformationUrlCache>;

export const useRestageStore = defineStore('restage', () => {
  const listingStore = useListingStore();

  const errorImageUrls = ref<Record<string, boolean>>({});
  const loadingImageUrls = ref<Record<string, boolean>>({});

  const transformationsByImageUrl = ref<Record<string, RestageTransformation>>({});
  const restagedImageUrlsByImageUrl = ref<RestagedUrlCache>({});

  // eslint-disable-next-line max-statements
  async function restageImage(
    imageUrl: string,
    transformation: RestageTransformation,
  ): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let isOvertime = false;
    try {
      unset(errorImageUrls.value, [imageUrl]);
      loadingImageUrls.value[imageUrl] = true;
      transformationsByImageUrl.value[imageUrl] = transformation;

      const restagedImageUrl = get(restagedImageUrlsByImageUrl.value, [imageUrl, transformation]);

      if (restagedImageUrl) {
        return;
      }

      const listing = listingStore.listing;
      if (!listing) throw new Error('Listing not found');

      const listingImageAttachments = getListingImages(listing);
      if (!listingImageAttachments) throw new Error('Listing image attachments not found');

      const fileName = listingImageAttachments.find(
        (attachment) => attachment.url === imageUrl,
      )?.file;

      const objectReference = listing.externalIds?.displayPropertyReferenceId;
      if (!objectReference) throw new Error('Object reference not found');

      const requestBody: RestageRequestPayload = {
        imageUrl,
        transformation,
        objectReference,
        fileName: fileName ?? '',
      };

      timer = setTimeout(() => {
        isOvertime = true;
        restageImageCallOvertime(transformation);
      }, OVERTIME_THRESHOLD.RESTAGE_IMAGE);

      restageImageCallStarted(transformation);
      const { cdnUrl } = await postRestageImage(requestBody);

      if (!has(restagedImageUrlsByImageUrl.value, imageUrl)) {
        restagedImageUrlsByImageUrl.value[imageUrl] = {};
      }

      set(restagedImageUrlsByImageUrl.value, [imageUrl, transformation], cdnUrl);

      if (isOvertime) {
        restageImageCallRecovered(transformation);
      }

      restageImageCallSucceeded(transformation);
    } catch (error) {
      if (timer) {
        const errorType = getRestageErrorType(error);
        restageImageCallFailed(transformation, errorType);
      }
      set(errorImageUrls.value, [imageUrl], true);
    } finally {
      if (timer) {
        clearTimeout(timer);
      }
      unset(loadingImageUrls.value, [imageUrl]);
    }
  }

  function undoImageTransformation(imageUrl: string): void {
    unset(transformationsByImageUrl.value, [imageUrl]);
  }

  function clearImageError(imageUrl: string): void {
    unset(errorImageUrls.value, [imageUrl]);
  }

  function clearImageTransformation(imageUrl: string): void {
    unset(transformationsByImageUrl.value, [imageUrl]);
  }

  function resetImageState(imageUrl: string): void {
    unset(errorImageUrls.value, [imageUrl]);
    unset(transformationsByImageUrl.value, [imageUrl]);
    unset(restagedImageUrlsByImageUrl.value, [imageUrl]);
  }

  function getRestagedImage(originalImage: HgImage): HgImage | undefined {
    const { src, alt, caption, sizes } = originalImage;

    const restageTransformation = get(transformationsByImageUrl.value, [src]);
    if (!restageTransformation) return;

    const restagedImageUrl = get(restagedImageUrlsByImageUrl.value, [src, restageTransformation]);

    if (!restagedImageUrl) return;

    return {
      alt: `[${restageTransformation}] ${alt}`,
      src: getCloudinaryUrlForTransformation(
        restagedImageUrl,
        HgCloudinaryNamedTransformation.SMALL,
      ),
      caption,
      srcset: getCloudinaryUrlVersions(restagedImageUrl),
      sizes,
    };
  }

  return {
    // state
    errorImageUrls,
    loadingImageUrls,
    transformationsByImageUrl,
    restagedImageUrlsByImageUrl,

    // getters
    getRestagedImage,

    // actions
    restageImage,
    undoImageTransformation,
    clearImageError,
    clearImageTransformation,
    resetImageState,
  };
});
